<template>
  <gas-page-shell :breadcrumb="['报表管理', '用量统计报表']">
    <div class="stat-row">
      <div v-for="item in statCards" :key="item.label" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }"><i :class="item.icon"></i></div>
        <div class="stat-body">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value">{{ item.value }}<span v-if="item.unit" class="unit">{{ item.unit }}</span></div>
        </div>
      </div>
    </div>

    <div class="search-panel">
      <el-form :inline="true" :model="query" size="small" label-width="72px" class="search-form">
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            style="width:380px"
            @change="onDateRangeChange"
          />
          <el-button class="date-shortcut" size="mini" @click="shiftDateRange(-1)">前一天</el-button>
          <el-button class="date-shortcut" size="mini" @click="shiftDateRange(1)">后一天</el-button>
        </el-form-item>
        <el-form-item label="站点名称">
          <el-select v-model="query.siteName" clearable filterable placeholder="请选择站点" style="width:200px">
            <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetSearch">重置</el-button>
          <el-button type="primary" @click="search">查询</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-panel">
      <div class="table-head">
        <span class="table-title">站点用量统计 <em>共 {{ total }} 条记录</em></span>
        <el-button type="success" size="small" icon="el-icon-download" @click="exportExcel">导出Excel</el-button>
      </div>
      <el-table
        :data="usageListRows"
        v-loading="loading"
        border
        size="small"
        :row-key="reportListRowKey"
        :span-method="reportUsageSpanMethod"
      >
        <el-table-column label="序号" width="60" align="center">
          <template slot-scope="{ row, $index }">{{ rowIndex($index, row) }}</template>
        </el-table-column>
        <el-table-column column-key="siteName" label="站点名称" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ reportSiteName(row) || '—' }}</template>
        </el-table-column>
        <el-table-column column-key="deviceName" label="设备名称" min-width="180">
          <template slot-scope="{ row }">{{ reportDeviceName(row) }}</template>
        </el-table-column>
        <el-table-column label="开始时间" min-width="150" align="center">
          <template slot-scope="{ row }">{{ row.beginTime || '—' }}</template>
        </el-table-column>
        <el-table-column label="截止时间" min-width="150" align="center">
          <template slot-scope="{ row }">{{ row.endTime || '—' }}</template>
        </el-table-column>
        <el-table-column label="标况总量累计(Nm³)" min-width="160" align="right">
          <template slot-scope="{ row }">{{ formatNum(row.stdTotalSum) }}</template>
        </el-table-column>
        <el-table-column label="工况总量累计(m³)" min-width="160" align="right">
          <template slot-scope="{ row }">{{ formatNum(row.workTotalSum) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination class="pager" background layout="total, sizes, prev, pager, next, jumper"
        :total="total" :current-page="query.pageNum" :page-size="query.pageSize"
        :page-sizes="[10, 20, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import reportFlowCommon, { formatNum, recentDateTimeRange } from '@/mixins/reportFlowCommon'
import reportTableMerge from '@/mixins/reportTableMerge'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import siteOptionsMixin from '@/mixins/siteOptions'
import { gasApi } from '@/api/gas'

export default {
  components: { GasPageShell },
  mixins: [reportFlowCommon, deviceTypeOptionsMixin, siteOptionsMixin, reportTableMerge],
  data() {
    const defaultRange = recentDateTimeRange()
    return {
      dateRange: [...defaultRange],
      projectCategoryOptions: [],
      query: {
        pageNum: 1,
        pageSize: 10,
        beginDate: defaultRange[0],
        endDate: defaultRange[1],
        projectCategoryName: '',
        deviceType: '',
        siteName: ''
      }
    }
  },
  created() {
    this.loadDeviceTypeOptions()
    this.loadSiteOptions()
    this.loadProjectCategoryOptions()
    this.loadStats()
    this.loadData()
  },
  methods: {
    formatNum,
    projectCategoryValue(row) {
      return typeof row === 'string' ? row : (row && row.categoryName) || ''
    },
    async loadProjectCategoryOptions() {
      try {
        const res = await gasApi.projectCategory.options()
        const rows = res.data || []
        this.projectCategoryOptions = rows.map(this.projectCategoryValue).filter(Boolean)
      } catch (e) {
        this.projectCategoryOptions = []
      }
    },
    onDateRangeChange(val) {
      if (val && val.length === 2) {
        this.query.beginDate = val[0]
        this.query.endDate = val[1]
      } else {
        this.query.beginDate = ''
        this.query.endDate = ''
      }
    },
    async loadStats() {
      const res = await gasApi.reportFlowUsage.stats(this.buildQuery())
      const d = res.data || {}
      this.statCards = [
        { label: '站点数', value: d.totalSites || 0, icon: 'el-icon-s-home', color: '#1677ff', bg: '#e8f3ff' },
        { label: '设备数', value: d.totalDevices || 0, icon: 'el-icon-odometer', color: '#722ed1', bg: '#f9f0ff' },
        { label: '标况总量累计', value: formatNum(d.stdTotalSum), unit: 'Nm³', icon: 'el-icon-s-opportunity', color: '#ff7d00', bg: '#fff7e8' },
        { label: '工况总量累计', value: formatNum(d.workTotalSum), unit: 'm³', icon: 'el-icon-data-line', color: '#00b42a', bg: '#e8ffea' }
      ]
    },
    async loadData() {
      this.loading = true
      try {
        const res = await gasApi.reportFlowUsage.list(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    resetSearch() {
      const defaultRange = recentDateTimeRange()
      this.dateRange = [...defaultRange]
      this.query = {
        pageNum: 1,
        pageSize: 10,
        beginDate: defaultRange[0],
        endDate: defaultRange[1],
        projectCategoryName: '',
        deviceType: '',
        siteName: ''
      }
      this.loadStats()
      this.loadData()
    },
    shiftDateRange(days) {
      if (!this.dateRange || this.dateRange.length !== 2) return
      const shift = value => {
        const d = new Date(String(value).replace(/-/g, '/'))
        d.setDate(d.getDate() + days)
        const p = n => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
      }
      const next = [shift(this.dateRange[0]), shift(this.dateRange[1])]
      this.dateRange = next
      this.onDateRangeChange(next)
      this.search()
    },
    async exportExcel() {
      const res = await gasApi.reportFlowUsage.export(this.buildQuery())
      this.downloadCsv(res.data || [], ['站点名称', '设备名称', '开始时间', '截止时间', '标况总量累计(Nm³)', '工况总量累计(m³)'],
        r => [this.reportSiteName(r), this.reportDeviceName(r), r.beginTime || '', r.endTime || '', r.stdTotalSum, r.workTotalSum], '用量统计报表')
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/report-flow.scss';
</style>
